/**
 * TenantAdminsPage — Manage domain administrator accounts and permissions.
 */
import { useMemo, useState, useEffect, type JSX } from 'react'
import { Column } from 'primereact/column'
import { confirmDialog } from 'primereact/confirmdialog'
import { MfaResetDialog } from '../../components/admin/MfaResetDialog.js'
import {
  Users, CheckCircle2, Globe, Plus, Search, RefreshCw,
  Eye, Pencil, Trash2, Shield, Mail, Phone, User, ShieldOff } from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import TenantAdminDrawer from '../../components/platform/TenantAdminDrawer.js'
import DataTable from '../../components/ui/DataTable.js'
import { useTenantAdmins, type TenantAdmin, type TenantAdminFormData } from '../../hooks/useTenantAdmins.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { formatInTimezone } from '../../utils/timezone.js'

/* ─── Stat Card ─── */
function StatCard({ label, value, icon: Icon, accent }: {
  label: string
  value: number
  icon: typeof Users
  accent: string
}) {
  return (
    <div className="relative bg-white rounded-sm border border-gray-100 p-5 overflow-hidden group hover:shadow-md hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-4">
        <div
          className="h-11 w-11 rounded-sm flex items-center justify-center shrink-0"
          style={{ background: `${accent}10` }}
        >
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: accent }}
      />
    </div>
  )
}

/* ─── Action Icon ─── */
function ActionIcon({ icon: Icon, label, onClick, color }: {
  icon: typeof Eye
  label: string
  onClick: () => void
  color: string
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      className="h-8 w-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 transition-all duration-150 hover:border-transparent hover:text-white"
      style={{ ['--color' as any]: color }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = (e.currentTarget as HTMLElement).style.getPropertyValue('--color')
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'
        ;(e.currentTarget as HTMLElement).style.borderColor = '#f3f4f6'
      }}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  )
}

/* ─── Status Badge ─── */
function StatusBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      Inactive
    </span>
  )
}

export default function TenantAdminsPage(): JSX.Element {
  const toast = useToast()
  const { list, get, create, update, remove, resetMfa } = useTenantAdmins()

  const [search, setSearch] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState<'create' | 'view' | 'edit'>('create')
  const [selectedAdmin, setSelectedAdmin] = useState<TenantAdmin | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<TenantAdmin[]>([])
  const [stats, setStats] = useState({ total: 0, active: 0 })

  const fetchAdmins = async (): Promise<void> => {
    try {
      setLoading(true)
      const response = await list(100, 0)
      setItems(response.admins)
      if (response.stats) setStats(response.stats)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to fetch tenant admins')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchAdmins() }, [])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(a => a.username?.toLowerCase().includes(q) || a.email.toLowerCase().includes(q))
  }, [items, search])

  const handleCreate = (): void => {
    setDrawerMode('create')
    setSelectedAdmin(undefined)
    setDrawerOpen(true)
  }

  const handleEdit = async (admin: TenantAdmin): Promise<void> => {
    setDrawerMode('edit')
    setSelectedAdmin(admin)
    setDrawerOpen(true)
    try { setSelectedAdmin(await get(admin.id)) } catch { /* non-fatal */ }
  }

  const handleView = async (admin: TenantAdmin): Promise<void> => {
    setDrawerMode('view')
    setSelectedAdmin(admin)
    setDrawerOpen(true)
    try { setSelectedAdmin(await get(admin.id)) } catch { /* non-fatal */ }
  }

  const handleSave = async (data: TenantAdminFormData): Promise<void> => {
    try {
      setLoading(true)
      if (drawerMode === 'edit' && selectedAdmin) {
        await update(selectedAdmin.id, data)
        toast.success('Admin updated')
      } else {
        await create(data)
        toast.success('Admin created successfully')
      }
      setDrawerOpen(false)
      await fetchAdmins()
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Failed to save admin'
      if (!errorMsg.includes('Failed query') && !errorMsg.includes('duplicate key')) {
        toast.error(errorMsg)
      }
    } finally {
      setLoading(false)
    }
  }

  const [mfaResetTarget, setMfaResetTarget] = useState<TenantAdmin | null>(null)
  const handleResetMfa = (admin: TenantAdmin): void => setMfaResetTarget(admin)
  const submitResetMfa = async (password: string, loginId: string | undefined): Promise<void> => {
    if (!mfaResetTarget) return
    await resetMfa(mfaResetTarget.id, password, loginId)
    toast.success('MFA reset')
    setMfaResetTarget(null)
  }

  const handleDelete = (admin: TenantAdmin): void => {
    confirmDialog({
      message: `Delete admin "${admin.firstName} ${admin.lastName}"?`,
      header: 'Delete Admin',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          setLoading(true)
          await remove(admin.id)
          toast.success('Admin deleted')
          await fetchAdmins()
        } catch (e) {
          toast.error(e instanceof Error ? e.message : 'Failed to delete admin')
        } finally { setLoading(false) }
      }
    })
  }

  return (
    <RequirePermission permission="platform:tenants_manage">
      <div className="w-full space-y-6 px-4 pb-10">
        <MfaResetDialog
          open={mfaResetTarget !== null}
          targetName={mfaResetTarget ? `${mfaResetTarget.firstName} ${mfaResetTarget.lastName}` : ''}
          onClose={() => setMfaResetTarget(null)}
          onSubmit={submitResetMfa}
        />
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Tenant Admins</h1>
            <p className="text-sm text-gray-400 mt-1">Manage domain administrator accounts and permissions.</p>
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md"
          >
            <Plus className="h-4 w-4" />
            Add Admin
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard label="Total Admins" value={stats.total} icon={Users} accent="#3454cf" />
          <StatCard label="Active Admins" value={stats.active} icon={CheckCircle2} accent="#10b981" />
          <StatCard label="Domains Covered" value={stats.total} icon={Globe} accent="#f6931c" />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="relative w-80">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
            <input
              type="text"
              placeholder="Search by username or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-11 pl-10 pr-4 text-sm bg-white border border-gray-200 rounded-sm text-gray-700 placeholder:text-gray-300 focus:border-[#f6931c] focus:ring-[3px] focus:ring-[#f6931c]/10 focus:outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400 font-medium">{visible.length} admins</span>
            <div className="w-px h-4 bg-gray-200" />
            <button
              onClick={() => { setSearch(''); void fetchAdmins() }}
              className="h-9 w-9 flex items-center justify-center rounded-sm text-gray-400 hover:text-[#3454cf] hover:bg-[#3454cf]/5 transition-all border border-gray-200 hover:border-[#3454cf]/20"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Table */}
        <DataTable
          value={visible}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
          emptyMessage="No administrators found."
        >
          <Column
            header="Admin"
            sortable
            body={(admin: TenantAdmin) => (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
                  <User className="h-5 w-5 text-[#3454cf]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{admin.firstName} {admin.lastName}</p>
                  <p className="text-xs text-gray-400">@{admin.username || 'unknown'}</p>
                </div>
              </div>
            )}
            style={{ width: '20%' }}
          />
          <Column
            header="Domain"
            body={(admin: TenantAdmin) => (
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-gray-300" />
                <div>
                  <p className="text-sm font-medium text-gray-700">{admin.tenantName || '—'}</p>
                  <p className="text-xs text-gray-300">{admin.tenantId?.split('-')[0]}</p>
                </div>
              </div>
            )}
          />
          <Column
            header="Privileges"
            body={(admin: TenantAdmin) => (
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-gray-300" />
                <span className="text-sm font-medium text-gray-700">{admin.privilegeName || '—'}</span>
              </div>
            )}
          />
          <Column
            header="Contact"
            body={(admin: TenantAdmin) => (
              <div className="flex flex-col gap-0.5 text-xs">
                <div className="flex items-center gap-1.5 text-gray-500">
                  <Mail className="h-3 w-3" />
                  {admin.email}
                </div>
                {admin.mobileNumber && (
                  <div className="flex items-center gap-1.5 text-gray-400">
                    <Phone className="h-3 w-3" />
                    {admin.mobileNumber}
                  </div>
                )}
              </div>
            )}
          />
          <Column
            field="status"
            header="Status"
            sortable
            body={(admin: TenantAdmin) => <StatusBadge active={admin.status} />}
          />
          <Column
            field="createdAt"
            header="Created"
            sortable
            body={(admin: TenantAdmin) => (
              <span className="text-xs text-gray-400">
                {formatInTimezone(admin.createdAt, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-US')}
              </span>
            )}
          />
          <Column
            header="Actions"
            body={(admin: TenantAdmin) => (
              <div className="flex items-center gap-1">
                <ActionIcon icon={Eye} label="View" onClick={() => handleView(admin)} color="#3454cf" />
                <ActionIcon icon={Pencil} label="Edit" onClick={() => void handleEdit(admin)} color="#3454cf" />
                <ActionIcon icon={ShieldOff} label="Reset MFA" onClick={() => handleResetMfa(admin)} color="#f59e0b" />
                <ActionIcon icon={Trash2} label="Delete" onClick={() => handleDelete(admin)} color="#ef4444" />
              </div>
            )}
            style={{ width: '160px' }}
          />
        </DataTable>
      </div>

      <TenantAdminDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        mode={drawerMode}
        admin={selectedAdmin}
        onSave={handleSave}
        onDelete={async (id) => {
          try {
            setLoading(true)
            await remove(id)
            toast.success('Admin deleted')
            await fetchAdmins()
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Failed to delete admin')
          } finally { setLoading(false) }
        }}
        loading={loading}
      />
    </RequirePermission>
  )
}
